/**
 * Utility functions for managing feedback modal display logic
 * @namespace feedbackModalUtils
 */

// Base storage key for tracking when feedback modal was last shown (will be made user-specific)
const FEEDBACK_MODAL_STORAGE_KEY_BASE = 'feedback_modal_last_shown';

// Number of months between feedback modal displays
const MONTHS_BETWEEN_DISPLAYS = 3;

/**
 * Get the user-specific storage key for tracking feedback modal display
 * @private
 * @param {number|string} userId - The user's ID
 * @returns {string} - User-specific storage key
 */
function getStorageKey(userId) {
    if (!userId) {
        // Fallback to base key if no user ID (shouldn't happen in normal flow)
        return FEEDBACK_MODAL_STORAGE_KEY_BASE;
    }
    return `${FEEDBACK_MODAL_STORAGE_KEY_BASE}_${userId}`;
}

/**
 * Check if a user is an active user
 * @param {Object} userStore - The user store instance
 * @returns {boolean} - True if user is active
 */
export function isActiveUser(userStore) {
    if (!userStore) return false;
    
    // User must be authenticated
    if (!userStore.isAuthenticated) return false;
    
    // User object must exist
    if (!userStore.user) return false;
    
    // Check if user is deactivated (if field exists)
    if (userStore.user.deactivated_at) return false;
    
    return true;
}

/**
 * Get the user's join date (created_at or hire_date as fallback)
 * @param {Object} user - The user object
 * @returns {Date|null} - The join date or null if not available
 */
export function getUserJoinDate(user) {
    if (!user) return null;
    
    // Prefer created_at, fallback to hire_date
    const dateString = user.created_at || user.hire_date;
    if (!dateString) return null;
    
    const joinDate = new Date(dateString);
    if (isNaN(joinDate.getTime())) return null;
    
    return joinDate;
}

/**
 * Normalize a date to the start of the day (midnight) for date-only comparisons
 * @private
 * @param {Date} date - The date to normalize
 * @returns {Date} - Date normalized to start of day
 */
function normalizeToStartOfDay(date) {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
}

/**
 * Add months to a date
 * @private
 * @param {Date} date - The base date
 * @param {number} months - Number of months to add
 * @returns {Date} - New date with months added
 */
function addMonths(date, months) {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
}

/**
 * Get the date when the feedback modal was last shown for a specific user
 * @param {number|string} userId - The user's ID
 * @returns {Date|null} - The last shown date or null
 */
export function getLastShownDate(userId) {
    if (!userId) return null;
    
    try {
        const storageKey = getStorageKey(userId);
        const lastShownString = localStorage.getItem(storageKey);
        if (!lastShownString) return null;
        
        const lastShown = new Date(lastShownString);
        if (isNaN(lastShown.getTime())) return null;
        
        return lastShown;
    } catch (error) {
        console.warn('Error getting last feedback modal shown date:', error);
        return null;
    }
}

/**
 * Store the current date as when the feedback modal was last shown for a specific user
 * Stores the normalized date (start of day) to ensure consistent calendar date comparisons
 * @param {number|string} userId - The user's ID
 */
export function setLastShownDate(userId) {
    if (!userId) {
        console.warn('Cannot set feedback modal last shown date: user ID is required');
        return;
    }
    
    try {
        const storageKey = getStorageKey(userId);
        const now = normalizeToStartOfDay(new Date());
        localStorage.setItem(storageKey, now.toISOString());
    } catch (error) {
        console.warn('Error setting last feedback modal shown date:', error);
    }
}

/**
 * Check if enough time has passed since the last display
 * @private
 * @param {Date} lastShownDate - The date when modal was last shown
 * @returns {boolean} - True if 3 months have passed (based on calendar date)
 */
function hasThreeMonthsPassed(lastShownDate) {
    if (!lastShownDate) return true; // Never shown before
    
    // Normalize dates to start of day for calendar date comparison
    const normalizedLastShown = normalizeToStartOfDay(lastShownDate);
    const now = normalizeToStartOfDay(new Date());
    const threeMonthsFromLastShown = normalizeToStartOfDay(
        addMonths(normalizedLastShown, MONTHS_BETWEEN_DISPLAYS)
    );
    
    return now >= threeMonthsFromLastShown;
}

/**
 * Check if user has been active for at least 3 months
 * @private
 * @param {Date} joinDate - The user's join date
 * @returns {boolean} - True if user has been active for 3+ months (based on calendar date)
 */
function hasBeenActiveForThreeMonths(joinDate) {
    if (!joinDate) return false;
    
    // Normalize dates to start of day for calendar date comparison
    // Example: Joined Aug 9 -> Show on Nov 9 (same calendar date, 3 months later)
    const normalizedJoinDate = normalizeToStartOfDay(joinDate);
    const now = normalizeToStartOfDay(new Date());
    const threeMonthsFromJoin = normalizeToStartOfDay(
        addMonths(normalizedJoinDate, MONTHS_BETWEEN_DISPLAYS)
    );
    
    return now >= threeMonthsFromJoin;
}

/**
 * Main function to determine if the feedback modal should be shown
 * @param {Object} userStore - The user store instance
 * @returns {boolean} - True if modal should be shown
 */
export function shouldShowFeedbackModal(userStore) {
    // (1) Check if user is active
    if (!isActiveUser(userStore)) {
        return false;
    }
    
    const user = userStore.user;
    
    // Get user ID - required for user-specific storage
    if (!user || !user.id) {
        // Cannot track per-user without user ID
        return false;
    }

    // Check availability based on snooze or completion
    const userId = user.id;

    // Check if snoozed
    if (isFeedbackSnoozed(userId)) {
        return false;
    }
    
    // Get user's join date
    const joinDate = getUserJoinDate(user);
    if (!joinDate) {
        // If we can't determine join date, don't show modal
        return false;
    }
    
    // Get last shown date for this specific user
    const lastShownDate = getLastShownDate(userId);
    
    // (2) First form display: Check if user has been active for at least 3 months
    if (!lastShownDate) {
        // Never shown before - check if 3 months have passed since join
        return hasBeenActiveForThreeMonths(joinDate);
    }
    
    // (3) Subsequent displays: Check if 3 months have passed since last shown
    return hasThreeMonthsPassed(lastShownDate);
}

/**
 * Check if feedback is currently snoozed for the user
 * @private
 * @param {number|string} userId 
 * @returns {boolean}
 */
function isFeedbackSnoozed(userId) {
    try {
        const storageKey = getStorageKey(userId) + '_snooze';
        const snoozeUntilString = localStorage.getItem(storageKey);
        
        if (!snoozeUntilString) return false;
        
        const snoozeUntil = new Date(snoozeUntilString);
        const now = new Date();
        
        return now < snoozeUntil;
    } catch (error) {
        console.warn('Error checking feedback snooze:', error);
        return false;
    }
}

/**
 * Snooze the feedback modal for a specified number of days
 * @param {number|string} userId 
 * @param {number} days 
 */
export function setFeedbackSnooze(userId, days = 30) {
    if (!userId) return;
    
    try {
        const storageKey = getStorageKey(userId) + '_snooze';
        const snoozeUntil = new Date();
        snoozeUntil.setDate(snoozeUntil.getDate() + days);
        
        localStorage.setItem(storageKey, snoozeUntil.toISOString());
    } catch (error) {
        console.warn('Error setting feedback snooze:', error);
    }
}

